<script lang='ts'>
  import { onMount } from 'svelte';
  import { link, push } from 'svelte-spa-router';
  import { location } from '../../framework/js/spaLocation';
  import { api } from '../../framework/js/api.js';
  import BriefList from "./BriefList.svelte";
  import BriefView from "./BriefView.svelte";
  import BriefAdd from "./BriefAdd.svelte";
  import Brief from "./Brief.svelte";
  import FlowModal from "../../framework/components/FlowModal.svelte";
  import { BriefStore } from '../../stores/BriefStore';
  import { AppPageHeader, AgentHero } from '../../framework/components/app';

  let currentBriefId: string | null = null;
  let hasItems: boolean | null = null;
  let totalCount: number | null = null;

  $: {
    const path = $location;
    const match = path.match(/\/briefs\/([^\/]+)/);
    currentBriefId = match ? match[1] : null;
  }

  // Detect brief modal routes
  $: modalMode = (() => {
    const path = $location || '';
    if (path === '/brief-new') return { kind: 'new' as const };
    const m = path.match(/^\/brief\/([^\/]+)\/(continue|edit)$/);
    if (m) return { kind: 'flow' as const, id: m[1] };
    return null;
  })();

  function handleModalClose() {
    if (modalMode && modalMode.kind === 'flow' && modalMode.id) {
      push(`/briefs/${modalMode.id}`);
    } else {
      push('/briefs');
    }
  }

  async function probe() {
    try {
      const res: any = await api.getXploritBriefs('', 'created', 'desc');
      if (res?.success && res?.data?.list) {
        const list = res.data.list;
        BriefStore.set(list);
        totalCount = list.length;
        hasItems = list.length > 0;
      } else {
        hasItems = false;
      }
    } catch {
      hasItems = false;
    }
  }

  onMount(() => {
    if (!currentBriefId) probe();
  });
</script>

<div class="briefs-page">
  {#if currentBriefId}
    {#key currentBriefId}
      <BriefView briefId={currentBriefId} />
    {/key}
  {:else if hasItems === null}
    <!-- probing -->
  {:else if hasItems}
    <AppPageHeader
      title="Built-to-Brief"
      subtitle="Build a scored brief from scratch."
      actionLabel="New brief"
      actionHref="/brief-new"
    />
    <BriefList />
  {:else}
    <AgentHero
      title="Built-to-Brief"
      subtitle="Build a scored research brief from a blank page, guided end to end."
      features={[
        'Structured prompts across every research dimension',
        'Scoring and gap detection as you write',
        'Version history with exportable output',
      ]}
      historyHref="/briefs"
      historyLabel="View past briefs"
      historyCount={totalCount}
    >
      <div slot="creator" class="creator-card">
        <h2 class="creator-title">Start your first brief</h2>
        <p class="creator-sub">Name your project. We'll take you through the rest.</p>
        <a use:link href="/brief-new" class="creator-cta">
          <span>Create brief</span>
          <i class="angle right icon" aria-hidden="true"></i>
        </a>
      </div>
    </AgentHero>
  {/if}
</div>

{#if modalMode}
  <FlowModal on:close={handleModalClose}>
    {#if modalMode.kind === 'new'}
      <BriefAdd />
    {:else}
      {#key modalMode.id}
        <Brief params={{ id: modalMode.id }} />
      {/key}
    {/if}
  </FlowModal>
{/if}

<style>
  .briefs-page {
    padding: var(--space-5) var(--space-6);
  }
  @media (max-width: 768px) {
    .briefs-page { padding: var(--space-4); }
  }

  .creator-card {
    background: var(--surface);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-card);
    box-shadow: var(--shadow-pop);
    padding: var(--space-6);
    text-align: center;
  }

  .creator-title {
    font-size: var(--text-xl);
    font-weight: 500;
    color: var(--grey);
    margin: 0 0 var(--space-2) 0;
  }

  .creator-sub {
    font-size: var(--text-sm);
    color: var(--grey-secondary);
    margin: 0 0 var(--space-5) 0;
  }

  .creator-cta {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-3) var(--space-6);
    background: var(--primary);
    color: var(--white);
    border-radius: var(--radius-cta);
    text-decoration: none;
    font-weight: 500;
    box-shadow: var(--shadow-cta);
    transition: filter .15s;
  }

  .creator-cta:hover { filter: brightness(.95); color: var(--white); }
  .creator-cta:focus-visible { outline: none; box-shadow: var(--focus-ring); }
</style>
